import { useMemo, useRef, useState } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { TooltipContentProps } from "recharts";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import {
  Button,
  cn,
  Icon,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@nexus/ui";
import type {
  BigQueryUsageBucket,
  BigQueryUsageBucketTenant,
  BigQueryUsageTenantBranding,
  BigQueryUsageTenantKind,
  BigQueryUsageTimeseries,
} from "@/features/bigquery-usage/api/bigquery-usage.api";
import { formatPreciseDurationMs } from "@/features/events/lib/event-timing";
import { SegmentedToggle } from "@/features/llm-usage/components/SegmentedToggle";
import { TenantMark } from "@/features/llm-usage/components/TenantMark";
import { formatBytesCompact } from "../lib/bytes";
import { serviceBandsForBucket, UNKNOWN_SERVICE } from "../lib/service-rollup";
import { humanizeBigQuerySlug } from "../lib/slug-labels";
import { tenantBrandingKey } from "../lib/tenant-branding";
import { maxBytesBilled, railStep } from "../lib/zoom-rail-intensity";

/** What the bars are cut by. Feature opens because it is the owner of the spend. */
export type BigQueryStackBy = "feature" | "tenant" | "service" | "cacheHit";

/**
 * The dimension half of a band's narrowing, in the shape the search params take.
 * Service is absent on purpose — see the service branch of `bucketBands`.
 */
export type BigQueryBandNarrowing =
  | { dimension: "feature"; feature: string }
  | { dimension: "tenant"; kind: BigQueryUsageTenantKind; tenantId?: string }
  | { dimension: "cacheHit"; cacheHit: boolean };

/**
 * What a click on the chart asks for. R17's two targets differ by exactly one
 * field: a band carries a `narrowing` and narrows to the intersection, while a
 * tick or the plot area carries only the span.
 */
export interface BigQueryZoomTarget {
  since: string;
  until: string;
  narrowing?: BigQueryBandNarrowing;
}

interface BigQueryChartProps {
  data: BigQueryUsageTimeseries | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /** True while TanStack holds the previous window via `keepPreviousData`. */
  isPlaceholderData: boolean;
  /** From `/features`, for the service stack. Uncovered features roll to unknown. */
  serviceByFeature: ReadonlyMap<string, string>;
  /**
   * Branding per tenant, keyed by `tenantBrandingKey`. It comes from the totals
   * read rather than the buckets: the timeseries carries a tenant once per
   * bucket, and a logo does not need signing that many times.
   */
  tenantBranding: ReadonlyMap<string, BigQueryUsageTenantBranding>;
  onZoom: (target: BigQueryZoomTarget) => void;
}

/** Matches the Events activity chart, which this sits alongside in the tab. */
const CHART_HEIGHT_PX = 132;
const BYTES_AXIS_WIDTH = 52;
const DURATION_AXIS_WIDTH = 44;
const CHART_TOP_MARGIN = 16;
const CHART_RIGHT_MARGIN = 4;
const Y_AXIS_TOP_PADDING = 8;
const AXIS_MIN_TICK_GAP = 28;
const MS_PER_MINUTE = 60 * 1000;
const MINUTES_PER_DAY = 60 * 24;

/**
 * The categorical ramp minus the orange the p95 line owns — a band wearing the
 * line's colour would read as its area. Assignment is by rank within the window,
 * so the busiest band keeps its colour as the range and filters change.
 */
const BAND_COLORS = [
  "var(--chart-green)",
  "var(--chart-purple)",
  "var(--chart-yellow)",
  "var(--chart-teal)",
  "var(--chart-red)",
  "var(--chart-blue)",
  "var(--chart-olive)",
] as const;
const P95_COLOR = "var(--chart-orange)";

/**
 * Past the palette, the remaining bands fold into one grey one: recycling a
 * colour would make two different features look like the same series, and the
 * tables below still list every one individually.
 */
const OTHER_KEY = "__other__";
const CATCH_ALL_COLOR = "var(--muted-foreground)";

/**
 * The unknown-service band is a catch-all too, and it means something else
 * entirely — a gap in the service registry rather than a series too small to
 * colour. So it is drawn hatched rather than merely grey, and it never folds
 * into the long tail: doing so would make the two indistinguishable.
 */
const HATCH_PATTERN_ID = "bq-unknown-service-hatch";

/** The legend is a single dense line, so the mark stays under the cap height. */
const LEGEND_MARK_SIZE = 14;

/**
 * The zoom rail's ramp, indexed by `railStep`. One neutral at rising strength:
 * the rail sits under the axis as chrome, so grading it lets the heavy hours
 * stand out without putting a second palette next to the bars. A quiet hour is
 * the hairline at the bottom — weaker than the faintest hour that ran anything.
 */
const RAIL_STEP_CLASS = [
  "bg-border",
  "bg-muted-foreground/20",
  "bg-muted-foreground/40",
  "bg-muted-foreground/60",
  "bg-muted-foreground/80",
] as const;

/**
 * Short, because the rail is a 6px strip: a pointer that has landed on a cell
 * this thin was aimed there, and a wait long enough to feel deliberate reads as
 * the tooltip having missed the hover.
 */
const RAIL_TOOLTIP_DELAY_MS = 120;

const AXIS_TICK = { fontSize: 10, fill: "var(--muted-foreground)" } as const;
const LABEL_CLASS = "text-[11px] uppercase tracking-wider text-muted-foreground";

interface ChartBand {
  key: string;
  label: string;
  bytesBilled: number;
  /** Absent for a catch-all, which stands for no single value to narrow to. */
  narrowing?: BigQueryBandNarrowing;
  /** The slug the label was derived from, kept for the legend's title. */
  slug?: string;
  /** A named tenant's branding. Absent for both specials and for every other split. */
  branding?: BigQueryUsageTenantBranding;
}

/**
 * What the zoom rail needs from a band: what to call it, and what it narrows to.
 * The long-tail band is a fold of several, so it has no total of its own — and
 * this is the shape that means it does not have to invent one.
 */
type RailBand = Pick<ChartBand, "key" | "label" | "narrowing">;

interface ChartPoint {
  bucketStart: string;
  /** Exclusive, derived from the bucket width the response declares. */
  bucketEnd: string;
  label: string;
  rangeLabel: string;
  bytesBilled: number;
  queries: number;
  p95DurationMs: number | null;
  bandValues: Record<string, number>;
  /** Only the bands this bucket actually carries, with the totals they rank by. */
  bands: ChartBand[];
}

/** A point with the long tail folded in, which is what the plot and rail read. */
interface FoldedChartPoint extends Omit<ChartPoint, "bands"> {
  bands: RailBand[];
}

function tenantBandLabel(
  kind: BigQueryUsageTenantKind,
  tenantName: string | null,
  tenantId: string | null,
  t: TFunction,
): string {
  if (kind === "platform") return t("admin.bigQueryUsage.chart.platform", "Platform");
  if (kind === "unattributed") return t("admin.bigQueryUsage.chart.unattributed", "Unattributed");
  // A tenant deleted since the query ran keeps its id, which still traces.
  return tenantName ?? tenantId ?? "";
}

/**
 * Only a named tenant has branding to draw. The two specials are categories
 * rather than orgs, and a tenant whose row is gone has nothing but a cuid —
 * which must never become initials.
 */
function tenantBandBranding(
  entry: BigQueryUsageBucketTenant,
  tenantBranding: ReadonlyMap<string, BigQueryUsageTenantBranding>,
): BigQueryUsageTenantBranding | undefined {
  if (entry.kind !== "tenant" || entry.tenantName == null) return undefined;
  return tenantBranding.get(tenantBrandingKey(entry));
}

function bucketBands(
  bucket: BigQueryUsageBucket,
  stackBy: BigQueryStackBy,
  serviceByFeature: ReadonlyMap<string, string>,
  tenantBranding: ReadonlyMap<string, BigQueryUsageTenantBranding>,
  t: TFunction,
): ChartBand[] {
  if (stackBy === "feature") {
    return bucket.features.map((entry) => ({
      key: entry.feature,
      label: humanizeBigQuerySlug(entry.feature),
      slug: entry.feature,
      bytesBilled: entry.bytesBilled,
      narrowing: { dimension: "feature", feature: entry.feature },
    }));
  }

  if (stackBy === "tenant") {
    return bucket.tenants.map((entry) => ({
      key: tenantBrandingKey(entry),
      label: tenantBandLabel(entry.kind, entry.tenantName, entry.tenantId, t),
      branding: tenantBandBranding(entry, tenantBranding),
      bytesBilled: entry.bytesBilled,
      narrowing: {
        dimension: "tenant",
        kind: entry.kind,
        tenantId: entry.tenantId ?? undefined,
      },
    }));
  }

  if (stackBy === "service") {
    // Service has no narrowing of its own. The API and the search params both
    // take a single `feature`, and a service owns many, so there is no param a
    // service band could write — it is a way of cutting the bars, not a filter.
    // Its band therefore narrows by time alone, like the plot area.
    return serviceBandsForBucket(bucket.features, serviceByFeature).map((entry) => ({
      key: entry.service,
      label:
        entry.service === UNKNOWN_SERVICE
          ? t("admin.bigQueryUsage.chart.unknownService", "Unknown service")
          : humanizeBigQuerySlug(entry.service),
      // The unknown band is a translated category rather than a service, so it
      // has no slug of its own to fall back to.
      slug: entry.service === UNKNOWN_SERVICE ? undefined : entry.service,
      bytesBilled: entry.bytesBilled,
    }));
  }

  return bucket.cacheStates.map((entry) => ({
    key: entry.cacheHit ? "hit" : "miss",
    label: entry.cacheHit
      ? t("admin.bigQueryUsage.chart.cacheHit", "Cache hit")
      : t("admin.bigQueryUsage.chart.cacheMiss", "Cache miss"),
    bytesBilled: entry.bytesBilled,
    narrowing: { dimension: "cacheHit", cacheHit: entry.cacheHit },
  }));
}

/**
 * A bucket narrower than a day is a time; a day or wider is a date. "14:00"
 * across a 30-day window would repeat the same handful of labels and say nothing
 * about which day.
 */
function bucketLabel(bucketStart: string, bucketMinutes: number, locale: string): string {
  const date = new Date(bucketStart);
  return bucketMinutes >= MINUTES_PER_DAY
    ? date.toLocaleDateString(locale, { month: "short", day: "numeric" })
    : date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
}

function bucketRangeLabel(
  bucketStart: string,
  bucketEnd: string,
  bucketMinutes: number,
  locale: string,
): string {
  const from = bucketLabel(bucketStart, bucketMinutes, locale);
  const to = bucketLabel(bucketEnd, bucketMinutes, locale);
  return `${from} – ${to}`;
}

/** The maps that turn a point's band keys into rows a reader can name. */
interface BandLookups {
  bandLabels: Map<string, string>;
  bandColors: Map<string, string>;
  bandBranding: ReadonlyMap<string, BigQueryUsageTenantBranding>;
}

// Only what the rows are built from, rather than the whole Recharts surface: the
// rest of that shape is layout state this tooltip never reads.
interface BigQueryTooltipProps
  extends Pick<TooltipContentProps<number, string>, "active" | "payload">,
    BandLookups {}

/**
 * Exported for its own test: a Recharts tooltip only mounts off real layout and
 * pointer geometry, which jsdom does not give the chart, so the rows are asserted
 * against this component directly.
 */
export function BigQueryTooltip({ active, payload, ...lookups }: BigQueryTooltipProps) {
  if (!active || !payload?.length) return null;

  const point = payload[0]?.payload as FoldedChartPoint | undefined;
  if (!point) return null;

  return <BucketBreakdown point={point} className="tooltip-enter relative z-50" {...lookups} />;
}

/**
 * What one bucket was spent on: a row per band, then the query count and p95 the
 * bands do not carry.
 *
 * One component for both ways in, because a bucket is one fact — the plot's own
 * hover and the zoom rail's are two pointers at the same bar, and a reader who
 * learned to read one should not have to learn the other.
 */
function BucketBreakdown({
  point,
  bandLabels,
  bandColors,
  bandBranding,
  className,
}: BandLookups & { point: FoldedChartPoint; className?: string }) {
  const { t, i18n } = useTranslation();

  const rows = [...bandColors.keys()]
    .filter((key) => (point.bandValues[key] ?? 0) > 0)
    .map((key) => ({
      key,
      label: bandLabels.get(key) ?? key,
      value: formatBytesCompact(point.bandValues[key] ?? 0, i18n.language),
      color: bandColors.get(key) ?? CATCH_ALL_COLOR,
      branding: bandBranding.get(key),
    }));

  return (
    <div
      data-slot="bucket-breakdown"
      className={cn(
        "rounded-md border border-border bg-card px-3 py-2 text-xs shadow-md",
        className,
      )}
    >
      <p className="mb-1.5 font-medium text-foreground">{point.rangeLabel}</p>
      {point.bytesBilled === 0 ? (
        <p className="text-muted-foreground">
          {t("admin.bigQueryUsage.chart.emptyBucket", "No queries")}
        </p>
      ) : (
        <ul className="space-y-0.5">
          {rows.map((row) => (
            <li key={row.key} className="flex items-center gap-1.5 whitespace-nowrap">
              <span
                aria-hidden
                className="size-1.5 shrink-0 rounded-full"
                style={{ backgroundColor: row.color }}
              />
              {/* Beside the dot, never instead of it — the dot is what ties the
                  row to its band, and a logo is not a label. */}
              {row.branding ? (
                <TenantMark
                  name={row.branding.tenantName}
                  logoUrl={row.branding.logoUrl}
                  brandColor={row.branding.brandColor}
                  size={LEGEND_MARK_SIZE}
                />
              ) : null}
              <span className="text-muted-foreground">{row.label}</span>
              <span className="ml-auto pl-3 font-medium tabular-nums text-foreground">
                {row.value}
              </span>
            </li>
          ))}
          <li className="flex items-center gap-1.5 whitespace-nowrap border-t border-border pt-1 text-muted-foreground">
            <span>{t("admin.bigQueryUsage.chart.queries", "Queries")}</span>
            <span className="ml-auto pl-3 tabular-nums">
              {point.queries.toLocaleString(i18n.language)}
            </span>
          </li>
          {point.p95DurationMs != null ? (
            <li className="flex items-center gap-1.5 whitespace-nowrap">
              <span
                aria-hidden
                className="size-1.5 shrink-0 rounded-full"
                style={{ backgroundColor: P95_COLOR }}
              />
              <span className="text-muted-foreground">
                {t("admin.bigQueryUsage.chart.p95", "p95")}
              </span>
              <span className="ml-auto pl-3 font-medium tabular-nums text-foreground">
                {formatPreciseDurationMs(point.p95DurationMs)}
              </span>
            </li>
          ) : null}
        </ul>
      )}
    </div>
  );
}

/**
 * Bytes billed over the window the KPI strip totals, with p95 duration over it,
 * and the way down to a narrower question.
 *
 * The chart is also the tab's main zoom-in control, so every target it offers is
 * a real focusable button in the zoom rail beneath the plot rather than an SVG
 * rectangle: a `<rect>` takes a pointer but cannot take focus, and R34 does not
 * exempt charts. The rects stay clickable for pointer users and call the same
 * handlers.
 */
export function BigQueryChart({
  data,
  isLoading,
  isError,
  onRetry,
  isPlaceholderData,
  serviceByFeature,
  tenantBranding,
  onZoom,
}: BigQueryChartProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [stackBy, setStackBy] = useState<BigQueryStackBy>("feature");

  // Recharts fires the band's handler on the rect and then the chart's on the
  // wrapper it bubbles to. Stopping it there is what keeps a band click from
  // also registering as a plot click and widening what the operator asked for.
  const isBandClick = useRef(false);

  const points = useMemo<ChartPoint[]>(() => {
    if (!data) return [];
    const bucketMs = data.bucketMinutes * MS_PER_MINUTE;

    return data.buckets.map((bucket) => {
      const bucketEnd = new Date(Date.parse(bucket.bucketStart) + bucketMs).toISOString();
      const bands = bucketBands(bucket, stackBy, serviceByFeature, tenantBranding, t);
      const bandValues: Record<string, number> = {};
      for (const band of bands) {
        bandValues[band.key] = (bandValues[band.key] ?? 0) + band.bytesBilled;
      }

      return {
        bucketStart: bucket.bucketStart,
        bucketEnd,
        label: bucketLabel(bucket.bucketStart, data.bucketMinutes, locale),
        rangeLabel: bucketRangeLabel(bucket.bucketStart, bucketEnd, data.bucketMinutes, locale),
        bytesBilled: bucket.bytesBilled,
        queries: bucket.queries,
        p95DurationMs: bucket.p95DurationMs,
        bandValues,
        bands,
      };
    });
  }, [data, locale, serviceByFeature, stackBy, t, tenantBranding]);

  // Ranked by window total so the stack order and colours stay put as buckets
  // come and go on refetch. The unknown band is held out of the ranking: it is
  // exempt from the fold, so it must not consume a palette slot either.
  const { bandKeys, bandLabels, bandSlugs, bandBranding } = useMemo(() => {
    const totals = new Map<string, number>();
    const labels = new Map<string, string>();
    const slugs = new Map<string, string>();
    const branding = new Map<string, BigQueryUsageTenantBranding>();
    for (const point of points) {
      for (const band of point.bands) {
        totals.set(band.key, (totals.get(band.key) ?? 0) + band.bytesBilled);
        labels.set(band.key, band.label);
        if (band.slug != null) slugs.set(band.key, band.slug);
        if (band.branding != null) branding.set(band.key, band.branding);
      }
    }

    const hasUnknown = totals.delete(UNKNOWN_SERVICE);
    const ranked = [...totals.entries()].sort(([, a], [, b]) => b - a).map(([key]) => key);
    const named =
      ranked.length > BAND_COLORS.length
        ? [...ranked.slice(0, BAND_COLORS.length), OTHER_KEY]
        : ranked;

    labels.set(
      OTHER_KEY,
      stackBy === "tenant"
        ? t("admin.bigQueryUsage.chart.otherTenants", "Other tenants")
        : stackBy === "service"
          ? t("admin.bigQueryUsage.chart.otherServices", "Other services")
          : t("admin.bigQueryUsage.chart.otherFeatures", "Other features"),
    );

    return {
      bandKeys: hasUnknown ? [...named, UNKNOWN_SERVICE] : named,
      bandLabels: labels,
      bandSlugs: slugs,
      bandBranding: branding,
    };
  }, [points, stackBy, t]);

  const bandColors = useMemo(
    () =>
      new Map(
        bandKeys.map((key, index) => [
          key,
          key === OTHER_KEY || key === UNKNOWN_SERVICE
            ? CATCH_ALL_COLOR
            : BAND_COLORS[index % BAND_COLORS.length],
        ]),
      ),
    [bandKeys],
  );

  /** Bands past the palette are folded, so a point's values follow the key set. */
  const foldedPoints = useMemo<FoldedChartPoint[]>(() => {
    const named = new Set(bandKeys);
    return points.map((point) => {
      const bandValues: Record<string, number> = {};
      const bands: RailBand[] = [];
      for (const band of point.bands) {
        const isNamed = named.has(band.key);
        const key = isNamed ? band.key : OTHER_KEY;
        bandValues[key] = (bandValues[key] ?? 0) + band.bytesBilled;
        // The rail offers the folded band under its catch-all name, once.
        if (isNamed) bands.push(band);
        else if (!bands.some((entry) => entry.key === OTHER_KEY)) {
          bands.push({ key: OTHER_KEY, label: bandLabels.get(OTHER_KEY) ?? OTHER_KEY });
        }
      }
      return { ...point, bandValues, bands };
    });
  }, [bandKeys, bandLabels, points]);

  // Queries, not bytes: a cache hit bills zero, so a window of nothing but hits
  // has a byte total of zero and still ran every query the strip above counts.
  const hasQueries = points.some((point) => point.queries > 0);
  const hasBillableBytes = points.some((point) => point.bytesBilled > 0);
  const hasDuration = points.some((point) => point.p95DurationMs != null);

  const emitZoom = (point: FoldedChartPoint, band?: RailBand) => {
    onZoom({
      since: point.bucketStart,
      until: point.bucketEnd,
      ...(band?.narrowing ? { narrowing: band.narrowing } : {}),
    });
  };

  if (isError) {
    return (
      <section className="shrink-0 rounded-md border border-border bg-card">
        <div
          className="flex flex-col items-center justify-center gap-3 p-6"
          style={{ minHeight: CHART_HEIGHT_PX }}
        >
          <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Icon name="alert-circle" size="xs" colorVar="--destructive" aria-hidden />
            {t("admin.bigQueryUsage.chart.error", "Could not load the BigQuery timeseries.")}
          </span>
          <Button variant="outline" size="sm" onClick={onRetry}>
            {t("admin.bigQueryUsage.retry", "Try again")}
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section className="relative z-10 shrink-0 rounded-md border border-border bg-card">
      <div className="flex items-center gap-x-4 px-3 py-2">
        <span className={cn(LABEL_CLASS, "shrink-0")}>
          {t("admin.bigQueryUsage.chart.title", "Bytes billed")}
        </span>
        <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-x-3 gap-y-1">
          {bandKeys.map((key) => (
            <BandLegendEntry
              key={key}
              bandKey={key}
              color={bandColors.get(key) ?? CATCH_ALL_COLOR}
              label={bandLabels.get(key) ?? key}
              slug={bandSlugs.get(key)}
              branding={bandBranding.get(key)}
            />
          ))}
          {hasDuration ? (
            <BandLegendEntry
              bandKey="p95"
              color={P95_COLOR}
              label={t("admin.bigQueryUsage.chart.p95", "p95")}
            />
          ) : null}
        </div>
        <div className="shrink-0">
          <SegmentedToggle<BigQueryStackBy>
            value={stackBy}
            onValueChange={setStackBy}
            ariaLabel={t("admin.bigQueryUsage.chart.stackBy", "Stack by")}
            options={[
              { value: "feature", label: t("admin.bigQueryUsage.chart.byFeature", "Feature") },
              { value: "tenant", label: t("admin.bigQueryUsage.chart.byTenant", "Tenant") },
              { value: "service", label: t("admin.bigQueryUsage.chart.byService", "Service") },
              { value: "cacheHit", label: t("admin.bigQueryUsage.chart.byCacheHit", "Cache hit") },
            ]}
          />
        </div>
      </div>

      <div className="px-1 pb-2 pt-1">
        {isLoading && points.length === 0 ? (
          <Skeleton className="w-full rounded-sm" style={{ height: CHART_HEIGHT_PX }} />
        ) : !hasQueries || !hasBillableBytes ? (
          <div
            className="flex items-center justify-center px-3 text-center text-xs text-muted-foreground"
            style={{ height: CHART_HEIGHT_PX }}
          >
            {hasQueries
              ? t(
                  "admin.bigQueryUsage.chart.emptyBytes",
                  "No billable bytes in this window — a cache hit or a failed query bills nothing.",
                )
              : t("admin.bigQueryUsage.chart.empty", "No BigQuery queries in this window")}
          </div>
        ) : (
          <div
            className={cn("transition-opacity duration-200", isPlaceholderData && "opacity-40")}
            aria-busy={isPlaceholderData || undefined}
          >
            <ResponsiveContainer width="100%" height={CHART_HEIGHT_PX}>
              <ComposedChart
                data={foldedPoints}
                margin={{ top: CHART_TOP_MARGIN, right: CHART_RIGHT_MARGIN, left: 0, bottom: 0 }}
                onClick={(nextState) => {
                  if (isBandClick.current) {
                    isBandClick.current = false;
                    return;
                  }
                  // Recharts types the active index as a number or a category
                  // key, and only a numeric one addresses a bucket.
                  const index = Number(nextState?.activeTooltipIndex);
                  const point = Number.isInteger(index) ? foldedPoints[index] : undefined;
                  // The plot area and the axis mean the span and nothing else.
                  if (point) emitZoom(point);
                }}
              >
                <defs>
                  <pattern
                    id={HATCH_PATTERN_ID}
                    width={4}
                    height={4}
                    patternTransform="rotate(45)"
                    patternUnits="userSpaceOnUse"
                  >
                    <line
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="4"
                      stroke={CATCH_ALL_COLOR}
                      strokeWidth={2}
                    />
                  </pattern>
                </defs>
                <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={AXIS_TICK}
                  tickLine={false}
                  axisLine={{ stroke: "var(--border)" }}
                  interval="preserveStartEnd"
                  minTickGap={AXIS_MIN_TICK_GAP}
                />
                <YAxis
                  yAxisId="bytes"
                  tick={AXIS_TICK}
                  tickLine={false}
                  axisLine={false}
                  width={BYTES_AXIS_WIDTH}
                  tickFormatter={(value: number) => formatBytesCompact(value, locale)}
                  padding={{ top: Y_AXIS_TOP_PADDING, bottom: 0 }}
                />
                {hasDuration ? (
                  <YAxis
                    yAxisId="duration"
                    orientation="right"
                    tick={{ ...AXIS_TICK, fill: P95_COLOR }}
                    tickLine={false}
                    axisLine={false}
                    width={DURATION_AXIS_WIDTH}
                    tickFormatter={(value: number) => formatPreciseDurationMs(value)}
                    padding={{ top: Y_AXIS_TOP_PADDING, bottom: 0 }}
                  />
                ) : null}
                <ChartTooltip
                  content={(props) => (
                    <BigQueryTooltip
                      {...(props as TooltipContentProps<number, string>)}
                      bandLabels={bandLabels}
                      bandColors={bandColors}
                      bandBranding={bandBranding}
                    />
                  )}
                  cursor={{ fill: "var(--foreground)", fillOpacity: 0.04 }}
                  // Bars/grid are SVG siblings that otherwise paint over the HTML tooltip.
                  wrapperStyle={{ zIndex: 50 }}
                />
                {bandKeys.map((key) => (
                  <Bar
                    key={key}
                    yAxisId="bytes"
                    // A function key, because a feature or service name can carry
                    // dots that Recharts would read as a nested path.
                    dataKey={(point: FoldedChartPoint) => point.bandValues[key] ?? 0}
                    name={key}
                    stackId="bytes"
                    fill={
                      key === UNKNOWN_SERVICE
                        ? `url(#${HATCH_PATTERN_ID})`
                        : (bandColors.get(key) ?? CATCH_ALL_COLOR)
                    }
                    // CSS owns motion here; Recharts' own grow latches mid-flight
                    // on any parent re-render, which query updates cause.
                    isAnimationActive={false}
                    onClick={(_payload: unknown, index: number) => {
                      isBandClick.current = true;
                      const point = foldedPoints[index];
                      const band = point?.bands.find((entry) => entry.key === key);
                      if (point) emitZoom(point, band);
                    }}
                  />
                ))}
                {hasDuration ? (
                  <Line
                    yAxisId="duration"
                    type="monotone"
                    dataKey="p95DurationMs"
                    stroke={P95_COLOR}
                    strokeWidth={1.5}
                    dot={false}
                    activeDot={{ r: 2.5, strokeWidth: 0 }}
                    isAnimationActive={false}
                    connectNulls
                  />
                ) : null}
              </ComposedChart>
            </ResponsiveContainer>

            <ZoomRail
              points={foldedPoints}
              locale={locale}
              bandLabels={bandLabels}
              bandColors={bandColors}
              bandBranding={bandBranding}
              onZoomSpan={(point) => emitZoom(point)}
              onZoomBand={(point, band) => emitZoom(point, band)}
            />
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * The keyboard and screen-reader path into the plot.
 *
 * One cell per bucket, in the same order as the bars above, each holding the
 * bucket's own span target and one target per band it carries. The band targets
 * are `sr-only` until focused so the rail stays a thin axis strip rather than a
 * second chart, and become visible — with a ring — the moment they are the
 * focused control, which is what R34 asks for.
 */
function ZoomRail({
  points,
  locale,
  bandLabels,
  bandColors,
  bandBranding,
  onZoomSpan,
  onZoomBand,
}: BandLookups & {
  points: FoldedChartPoint[];
  locale: string;
  onZoomSpan: (point: FoldedChartPoint) => void;
  onZoomBand: (point: FoldedChartPoint, band: RailBand) => void;
}) {
  const { t } = useTranslation();
  const peak = maxBytesBilled(points);

  return (
    // Its own provider rather than the app's: the rail is the chart's, and the
    // chart is rendered on its own in tests, where an app-level one is absent.
    <TooltipProvider delayDuration={RAIL_TOOLTIP_DELAY_MS}>
      <div
        role="group"
        aria-label={t("admin.bigQueryUsage.chart.railLabel", "Narrow to a time span")}
        className="mt-1 flex items-stretch gap-px px-1"
      >
        {points.map((point) => (
          <div key={point.bucketStart} className="relative flex min-w-0 flex-1 flex-col">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={
                    point.queries === 0
                      ? t(
                          "admin.bigQueryUsage.chart.spanTargetEmpty",
                          "Time span {{range}}, no queries",
                          { range: point.rangeLabel },
                        )
                      : t("admin.bigQueryUsage.chart.spanTarget", "Time span {{range}}", {
                          range: point.rangeLabel,
                        })
                  }
                  onClick={() => onZoomSpan(point)}
                  className={cn(
                    "h-1.5 rounded-full outline-none transition-colors",
                    "focus-visible:ring-1 focus-visible:ring-ring",
                    // Every cell is a zoom target, so every cell answers the pointer —
                    // including a quiet hour, which is a span worth narrowing to.
                    "hover:bg-primary/60",
                    RAIL_STEP_CLASS[railStep(point, peak)],
                  )}
                />
              </TooltipTrigger>
              {/* Above the rail, where the bar it belongs to is, and stripped back
                  to a shell so the breakdown card is the whole tooltip rather
                  than a card inside the inverted default one. */}
              <TooltipContent
                side="top"
                sideOffset={6}
                className="max-w-none border-0 bg-transparent p-0 text-foreground shadow-none"
                // Stands in for Radix's hidden copy of the card, which would read
                // the rows back as one run-on line. Nothing is lost: this cell
                // already carries a target per band, each naming its own.
                aria-label={
                  point.queries === 0
                    ? t("admin.bigQueryUsage.chart.emptyBucket", "No queries")
                    : t(
                        "admin.bigQueryUsage.chart.spanSummary",
                        "{{bytes}} billed, {{queries}} queries",
                        {
                          bytes: formatBytesCompact(point.bytesBilled, locale),
                          queries: point.queries.toLocaleString(locale),
                        },
                      )
                }
              >
                <BucketBreakdown
                  point={point}
                  bandLabels={bandLabels}
                  bandColors={bandColors}
                  bandBranding={bandBranding}
                />
              </TooltipContent>
            </Tooltip>
            {point.bands.map((band) => (
              <button
                key={band.key}
                type="button"
                aria-label={t("admin.bigQueryUsage.chart.bandTarget", "{{band}} in {{range}}", {
                  band: band.label,
                  range: point.rangeLabel,
                })}
                onClick={() => onZoomBand(point, band)}
                className={cn(
                  "sr-only rounded-sm bg-card px-1 text-[11px] outline-none",
                  "focus:not-sr-only focus:absolute focus:z-20 focus:top-2 focus:whitespace-nowrap",
                  "focus-visible:ring-1 focus-visible:ring-ring",
                )}
              >
                {t("admin.bigQueryUsage.chart.bandTarget", "{{band}} in {{range}}", {
                  band: band.label,
                  range: point.rangeLabel,
                })}
              </button>
            ))}
          </div>
        ))}
      </div>
    </TooltipProvider>
  );
}

/**
 * A legend entry. The swatch carries a shape as well as a colour, because the
 * two catch-alls share the same grey and mean different things: one is a fold of
 * series too small to colour, the other is a gap in the service registry.
 *
 * A tenant's mark rides beside the swatch, never instead of it: the swatch is
 * what ties the entry to a band in the plot, and a logo an operator cannot see
 * must not be the only thing telling two tenants apart.
 */
function BandLegendEntry({
  bandKey,
  color,
  label,
  slug,
  branding,
}: {
  bandKey: string;
  color: string;
  label: string;
  slug?: string;
  branding?: BigQueryUsageTenantBranding;
}) {
  const { t } = useTranslation();
  const isUnknown = bandKey === UNKNOWN_SERVICE;
  const isLongTail = bandKey === OTHER_KEY;

  const title = isUnknown
    ? t(
        "admin.bigQueryUsage.chart.unknownServiceTitle",
        "Features the service registry does not cover. Their bytes are counted here rather than dropped.",
      )
    : isLongTail
      ? t(
          "admin.bigQueryUsage.chart.otherTitle",
          "Series past the chart's palette, folded into one band. The tables below list each of them.",
        )
      : (slug ?? label);

  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        aria-hidden
        data-band-shape={isUnknown ? "hatched" : "solid"}
        className={cn("size-1.5 shrink-0", isUnknown ? "rounded-none" : "rounded-full")}
        style={
          isUnknown
            ? {
                backgroundImage: `repeating-linear-gradient(45deg, ${color} 0 1px, transparent 1px 3px)`,
              }
            : { backgroundColor: color }
        }
      />
      {branding ? (
        <TenantMark
          name={branding.tenantName}
          logoUrl={branding.logoUrl}
          brandColor={branding.brandColor}
          size={LEGEND_MARK_SIZE}
        />
      ) : null}
      <span
        title={title}
        className={cn(LABEL_CLASS, "max-w-[22ch] truncate normal-case tracking-normal")}
      >
        {label}
      </span>
    </span>
  );
}
